import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import {
  PMO_FLOW_CLASS_COLORS,
  PMO_FLOW_PENDING_FILL,
  PMO_FLOW_PROJECT_TOTAL_COLOR,
  PMO_FLOW_WORK_TOTAL_COLOR,
  formatQuantile,
  hasMeasuredDays,
  highlightStyle,
  quantileLabel,
  type PmoFlowHighlightProps,
  type PmoFlowValueMode,
} from './segment-utils.js';
import type {
  PmoFlowProjectTotal,
  PmoFlowSegmentKey,
  PmoFlowSegmentStat,
  PmoFlowWorkTotal,
} from '../../../types/pmo-flow.js';

export interface PhaseSegmentTableProps extends PmoFlowHighlightProps {
  /** 表示順に並べ替え済みの区間統計 */
  segments: PmoFlowSegmentStat[];
  /** 実日数 / pt 正規化の表示モード */
  mode: PmoFlowValueMode;
  /** 案件あたりの作業合計。pt 正規化モードで作業工程の速さとして描く */
  workTotal?: PmoFlowWorkTotal | null;
  /** 案件全体のスループット。pt 正規化モードで作業合計の下に描く */
  projectTotal?: PmoFlowProjectTotal | null;
}

/**
 * 分位数 1 つ分の表示（ラベル + 値）。等幅・桁揃えで縦に並べても崩れないようにする。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ区間テーブルの分位数セル（p50/p75/p90 のラベルと値）を描画する
 * @graph-connects none
 */
function QuantileCell({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex flex-col items-end">
      <span className="text-[10px] uppercase text-[var(--color-text-muted)]">{label}</span>
      <span className="font-mono text-sm tabular-nums text-[var(--color-text-primary)]">
        {value}
      </span>
    </span>
  );
}

/**
 * 区間 1 行。計測済みなら分位数を、未計測なら理由テキストとバッジを表示する。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ区間1件分の行。区間名・区分・件数と分位数、または未計測理由とイベント収集待ちバッジを描画する
 * @graph-connects QuantileCell [calls] 分位数セル描画
 * @graph-connects Badge [calls] 計測状態バッジ描画
 */
function SegmentRow({
  segment,
  isHighlighted,
  onHighlight,
}: {
  segment: PmoFlowSegmentStat;
  isHighlighted: boolean;
  onHighlight: (key: PmoFlowSegmentKey | null) => void;
}) {
  const { t } = useTranslation();
  const measured = hasMeasuredDays(segment);
  // 区間別は実日数だけを出す。pt 正規化は区間単位では成立しないので作業合計 1 行に畳む
  const quantiles = segment.days;
  const chipColor = measured ? PMO_FLOW_CLASS_COLORS[segment.segmentClass] : PMO_FLOW_PENDING_FILL;

  return (
    <div
      className="pmo-segment-row flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-3 transition-colors duration-150 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
      style={isHighlighted ? highlightStyle(chipColor) : undefined}
      // hover に加えキーボード focus でもバー側と連動させる（マウス専用にしない）
      tabIndex={0}
      aria-label={t(`kpi.pmoFlow.segment.${segment.key}`)}
      data-testid={`pmo-flow-segment-row-${segment.key}`}
      data-highlighted={String(isHighlighted)}
      onMouseEnter={() => {
        onHighlight(segment.key);
      }}
      onMouseLeave={() => {
        onHighlight(null);
      }}
      onFocus={() => {
        onHighlight(segment.key);
      }}
      onBlur={() => {
        onHighlight(null);
      }}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block size-2.5 shrink-0 rounded-sm"
            style={{
              backgroundColor: chipColor,
              border: measured ? undefined : '1px dashed var(--color-text-muted)',
            }}
          />
          <span className="text-sm font-medium text-[var(--color-text-primary)]">
            {t(`kpi.pmoFlow.segment.${segment.key}`)}
          </span>
          <span className="text-xs text-[var(--color-text-muted)]">
            {t(`kpi.pmoFlow.segmentClass.${segment.segmentClass}`)}
          </span>
          {segment.availability === 'pending' && (
            <Badge variant="warning" size="sm">
              {t('kpi.pmoFlow.badge.pending')}
            </Badge>
          )}
          {segment.availability === 'sparse' && (
            <Badge variant="info" size="sm">
              {t('kpi.pmoFlow.badge.sparse')}
            </Badge>
          )}
        </div>
        {measured ? (
          <p className="mt-1 font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
            {t('kpi.pmoFlow.table.count', { value: segment.count })}
          </p>
        ) : (
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {segment.pendingReason ?? t('kpi.pmoFlow.table.pendingReasonFallback')}
          </p>
        )}
      </div>

      {measured && (
        <div className="flex shrink-0 items-end gap-4">
          {quantiles ? (
            <>
              <QuantileCell label="p50" value={formatQuantile(quantiles.p50)} />
              <QuantileCell label="p75" value={formatQuantile(quantiles.p75)} />
              <QuantileCell label="p90" value={formatQuantile(quantiles.p90)} />
              <span className="pb-0.5 text-xs text-[var(--color-text-muted)]">
                {t('kpi.pmoFlow.unit.days')}
              </span>
            </>
          ) : (
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('kpi.pmoFlow.table.noPerPoint')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * pt 正規化モードの合計行 1 本。区間別の行と違いバーの区間 1 本に対応しないので、
 * ハイライト連動は持たない。作業合計と案件全体スループットで共通の見た目にして、
 * 「どちらも案件単位の値で、違うのは分母だけ」だと並べて読めるようにする。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズのpt正規化合計行を、色チップ・名称・件数・p50/p25/p10のpt per日で描画する
 * @graph-connects QuantileCell [calls] 分位数セル描画
 * @graph-connects Badge [calls] サンプル僅少バッジ描画
 */
function PerPointTotalRow({
  rowKey,
  color,
  label,
  hint,
  classLabel,
  total,
}: {
  /** data-testid の接尾辞。バーの区間キーとは別の名前空間 */
  rowKey: string;
  /** 行頭の色チップ */
  color: string;
  /** 行の名称 */
  label: string;
  /** 名称の title に出す定義 */
  hint: string;
  /** 名称の右に添える区分名（作業 / 待ち+作業） */
  classLabel: string;
  /** 件数・サンプル十分性・規模正規化の分位数 */
  total: PmoFlowWorkTotal | PmoFlowProjectTotal;
}) {
  const { t } = useTranslation();
  const quantiles = total.daysPerPoint;
  const sparse = total.availability === 'sparse';

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
      data-testid={`pmo-flow-segment-row-${rowKey}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block size-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: color }}
          />
          <span className="text-sm font-medium text-[var(--color-text-primary)]" title={hint}>
            {label}
          </span>
          <span className="text-xs text-[var(--color-text-muted)]">{classLabel}</span>
          {sparse && (
            <Badge variant="info" size="sm">
              {t('kpi.pmoFlow.badge.sparse')}
            </Badge>
          )}
        </div>
        <p className="mt-1 font-mono text-xs tabular-nums text-[var(--color-text-muted)]">
          {t('kpi.pmoFlow.table.count', { value: total.count })}
        </p>
      </div>

      <div className="flex shrink-0 items-end gap-4">
        {quantiles ? (
          <>
            <QuantileCell
              label={quantileLabel('p50', 'perPoint')}
              value={formatQuantile(quantiles.p50, 'perPoint')}
            />
            <QuantileCell
              label={quantileLabel('p75', 'perPoint')}
              value={formatQuantile(quantiles.p75, 'perPoint')}
            />
            <QuantileCell
              label={quantileLabel('p90', 'perPoint')}
              value={formatQuantile(quantiles.p90, 'perPoint')}
            />
            <span className="pb-0.5 text-xs text-[var(--color-text-muted)]">
              {t('kpi.pmoFlow.unit.pointsPerDay')}
            </span>
          </>
        ) : (
          <span className="text-xs text-[var(--color-text-muted)]">
            {t('kpi.pmoFlow.table.noPerPoint')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * 作業合計 1 行。分母は「案件が実際に手を動かした営業日」で、待ちは入らない。
 *
 * 区間ごとに pt で割ると「その工程の速さ」ではなく「案件の大きさ」が出てしまうため
 * （実測で結合テスト実施の pt/日 は案件サイズと 0.279〜0.695 相関する）、
 * 分子の pt が案件全体の見積である以上、分母も案件全体の作業日に揃える。
 * 合算すると相関は 0.011〜0.033 まで落ちる。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズの作業合計（開発+結合テスト実施）1行を、件数と規模正規化のp50/p25/p10で描画する
 * @graph-connects PerPointTotalRow [calls] pt正規化合計行の描画
 */
function WorkTotalRow({ workTotal }: { workTotal: PmoFlowWorkTotal }) {
  const { t } = useTranslation();

  return (
    <PerPointTotalRow
      rowKey="workTotal"
      color={PMO_FLOW_WORK_TOTAL_COLOR}
      label={t('kpi.pmoFlow.workTotal.label')}
      hint={t('kpi.pmoFlow.workTotal.hint')}
      classLabel={t('kpi.pmoFlow.segmentClass.work')}
      total={workTotal}
    />
  );
}

/**
 * 案件全体のスループット 1 行。作業合計の下に並べ、分母の違いを対比させる。
 *
 * 分母は assign→結合テスト完了 の経過営業日そのもので、待ちで止まっていた時間も入る。
 * 作業合計が「動いている間の速さ」なのに対し、こちらは待ちを含めた案件の実効速度で、
 * 工程を速くしても待ち行列が伸びれば上がらない。分子は見積ではなく実績 pt。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズの案件全体スループット（待ち込みの経過営業日あたり実績pt）1行を、件数とp50/p25/p10で描画する
 * @graph-connects PerPointTotalRow [calls] pt正規化合計行の描画
 */
function ProjectTotalRow({ projectTotal }: { projectTotal: PmoFlowProjectTotal }) {
  const { t } = useTranslation();

  return (
    <PerPointTotalRow
      rowKey="projectTotal"
      color={PMO_FLOW_PROJECT_TOTAL_COLOR}
      label={t('kpi.pmoFlow.projectTotal.label')}
      hint={t('kpi.pmoFlow.projectTotal.hint')}
      classLabel={t('kpi.pmoFlow.segmentClass.mixed')}
      total={projectTotal}
    />
  );
}

/**
 * 区間別の一覧。件数と p50 / p75 / p90 を実日数 or pt 正規化（pt/日）で表示し、
 * 未計測区間は値の代わりに理由と「イベント収集待ち」バッジを出す。
 *
 * pt 正規化モードでは区間別の行を全て畳み、**案件単位の合計行 2 本**だけを出す。待ちは規模と
 * ほぼ無相関（実測 CORR 0.085 / -0.017）で pt で割った値に読み方が無く、作業も区間単位で
 * 割ると案件サイズを測ってしまう。案件全体の pt を分子に置く以上、分母も案件全体の
 * 作業日に揃えないと正規化が成立しない。その旨はカード側が注記する。
 * 行に hover すると `onHighlight` で区間キーを親へ通知し、バー側の同じ区間が光る
 * （逆向きは `highlightedKey` で受ける）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズ区間別の一覧。計測済み区間の件数とp50/p75/p90を表示し、未計測区間はイベント収集待ちの理由を明示する
 * @graph-connects SegmentRow [calls] 区間1行の描画
 * @graph-connects WorkTotalRow [calls] 作業合計1行の描画
 * @graph-connects ProjectTotalRow [calls] 案件全体スループット1行の描画
 */
export function PhaseSegmentTable({
  segments,
  mode,
  workTotal = null,
  projectTotal = null,
  highlightedKey = null,
  onHighlight,
}: PhaseSegmentTableProps) {
  const { t } = useTranslation();

  if (mode === 'perPoint') {
    // 作業合計が取れず案件全体だけ出せることもある（作業区間の境界が 1 つも揃わない母集団）。
    // 片方でも出せるなら空状態にはしない
    const hasAnyTotal = workTotal !== null || projectTotal !== null;
    return (
      <div className="space-y-2">
        {hasAnyTotal ? (
          <>
            {workTotal && <WorkTotalRow workTotal={workTotal} />}
            {projectTotal && <ProjectTotalRow projectTotal={projectTotal} />}
          </>
        ) : (
          <div className="flex items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] py-8">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </div>
    );
  }

  const shown = segments;

  if (shown.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] py-8">
        <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {shown.map((segment) => (
        <SegmentRow
          key={segment.key}
          segment={segment}
          isHighlighted={highlightedKey === segment.key}
          onHighlight={(key) => onHighlight?.(key)}
        />
      ))}
    </div>
  );
}
